// Who may see and change inbound routing. Kept tiny: the navigation imports it on every page.
//
// Seam: packages/domain ROLES doesn't list routing permissions yet, so the session's `permissions`
// carries them only as explicit grants. The API merges role defaults in
// apps/api/src/routing/permissions.ts; this mirrors that matrix from the session's own roles. The
// server stays the authority: a 403 from any routing call turns the page denied or view-only.
import type { Session } from '@bcio-voice/contracts';

export type RoutingPermission = 'routing:read' | 'routing:manage';

/** Same matrix as ROUTING_ROLE_PERMISSIONS in apps/api/src/routing/permissions.ts. */
export const ROUTING_ROLE_DEFAULTS: Readonly<Record<string, readonly RoutingPermission[]>> = Object.freeze({
  owner: ['routing:read', 'routing:manage'],
  admin: ['routing:read', 'routing:manage'],
  supervisor: ['routing:read', 'routing:manage'],
  agent: ['routing:read'],
  analyst: [],
});

export type RoutingAccess =
  | { kind: 'no_tenant' }
  | { kind: 'denied' }
  | { kind: 'allowed'; canManage: boolean };

/** The current workspace's role ids from the session's own memberships. */
export function sessionRoles(session: Pick<Session, 'tenant' | 'memberships'>): string[] {
  const tenantId = session.tenant?.id;
  return tenantId ? session.memberships.find(m => m.tenant_id === tenantId)?.role_ids ?? [] : [];
}

export function routingPermissions(session: Pick<Session, 'tenant' | 'memberships' | 'permissions'>): Set<string> {
  return new Set([...session.permissions, ...sessionRoles(session).flatMap(role => (Object.hasOwn(ROUTING_ROLE_DEFAULTS, role) ? ROUTING_ROLE_DEFAULTS[role] ?? [] : []))]);
}

export function routingAccess(session: Pick<Session, 'user' | 'tenant' | 'memberships' | 'permissions'>): RoutingAccess {
  if (!session.user || !session.tenant) return { kind: 'no_tenant' };
  const permissions = routingPermissions(session);
  if (!permissions.has('routing:read')) return { kind: 'denied' };
  return { kind: 'allowed', canManage: permissions.has('routing:manage') };
}
